import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { postBusRequest } from '../../util/api';
import { BUS_TYPES, BUS_TYPE_LABELS } from '../../util/commercialPassenger';
import { OPERATOR_TYPE, isOrganisationalBuyer } from '../../util/commercialInquiry';
import {
  validateBusRequestInquiry,
  CONDITION_PREFERENCE,
  BUDGET_BANDS,
} from '../../util/busRequestInquiry';
import {
  Modal,
  Form,
  FieldTextInput,
  FieldSelect,
  FieldRadioButton,
  FieldCheckbox,
  PrimaryButton,
} from '..';

import css from './BusRequestForm.module.css';

const MODAL_ID = 'BusRequestForm';

const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

// One key per request, reused on every retry, so a buyer who submits twice
// because the first attempt looked stuck does not become two leads.
const newIdempotencyKey = () =>
  typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `b${Date.now().toString(36)}${Math.random()
        .toString(36)
        .slice(2)}`;

/**
 * "Looking for a bus? Connect with Skip."
 *
 * A buyer who cannot find what they need in the listings tells us what they
 * are after, and a person follows up. It is a request — not a listing, not a
 * reservation, and not a finance application — and the confirmation says so,
 * because a school division that thinks a coach is being held for it will plan
 * around a vehicle nobody has set aside.
 *
 * Validation is `validateBusRequestInquiry`, the same function the server runs
 * before it stores or sends anything. One definition, so the form cannot
 * accept something the endpoint will reject, or the reverse.
 *
 * Consent gates submit. The button stays disabled until the box is ticked —
 * the same posture as FinanceLeadForm — and the server checks again, because a
 * disabled button is a courtesy, not a control.
 */
export default function BusRequestForm({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const idempotencyKeyRef = useRef(null);

  const isChecked = value => Array.isArray(value) && value.length > 0;

  // react-final-form keeps number inputs as strings; the shared validator
  // insists on a real integer, so the conversion happens here rather than the
  // validator loosening to accept '48'.
  const toCapacity = value => {
    if (value === '' || value == null) return null;
    const n = Number(value);
    return Number.isInteger(n) ? n : value;
  };

  const handleSubmit = async values => {
    setSubmitError('');

    const inquiryInput = {
      name: values.name,
      organization: values.organization,
      operatorType: values.operatorType,
      email: values.email,
      phone: values.phone,
      busType: values.busType,
      conditionPreference: values.conditionPreference,
      passengerCapacity: toCapacity(values.passengerCapacity),
      budgetBand: values.budgetBand || null,
      province: values.province || null,
      community: values.community,
      deliveryNotes: values.deliveryNotes,
      financingAssistanceRequested: values.financingAssistanceRequested === 'yes',
      consentToContact: isChecked(values.consentToContact),
    };

    const { valid, inquiry } = validateBusRequestInquiry(inquiryInput);
    if (!valid) {
      // Field-level validators should have caught these. Reaching here means
      // the two disagree, which is a bug worth surfacing rather than a silent
      // no-op that leaves the buyer pressing a dead button.
      setSubmitError(intl.formatMessage({ id: 'BusRequestForm.validationError' }));
      return;
    }

    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = newIdempotencyKey();

    try {
      const data = await postBusRequest({
        idempotencyKey: idempotencyKeyRef.current,
        ...inquiry,
      });

      if (data && data.success) {
        setSubmitted(true);
      } else {
        // The endpoint reports failure when nothing durable recorded the
        // request. Saying "received" here would be the same failure the
        // pre-approval flow already had: a buyer who waits for a call that
        // nobody was told to make.
        setSubmitError(intl.formatMessage({ id: 'BusRequestForm.notRecordedError' }));
      }
    } catch {
      setSubmitError(intl.formatMessage({ id: 'BusRequestForm.networkError' }));
    }
  };

  const requiredField = messageId => validators.required(intl.formatMessage({ id: messageId }));
  const emailValidators = validators.composeValidators(
    validators.required(intl.formatMessage({ id: 'BusRequestForm.emailRequired' })),
    validators.emailFormatValid(intl.formatMessage({ id: 'BusRequestForm.emailInvalid' }))
  );

  return (
    <Modal
      id={MODAL_ID}
      isOpen={isOpen}
      onClose={() => {
        setSubmitted(false);
        setSubmitError('');
        onClose();
      }}
      onManageDisableScrolling={(componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling))
      }
      usePortal
    >
      {submitted ? (
        <div className={css.confirmation} data-testid="bus-request-confirmation">
          <p className={css.confirmationTitle}>
            <FormattedMessage id="BusRequestForm.confirmationTitle" />
          </p>
          <p className={css.confirmationBody}>
            <FormattedMessage id="BusRequestForm.confirmationBody" />
          </p>
          <p className={css.compliance}>
            <FormattedMessage id="BusRequestForm.confirmationNotAHold" />
          </p>
        </div>
      ) : (
        <>
          <p className={css.title}>
            <FormattedMessage id="BusRequestForm.title" />
          </p>
          <p className={css.body}>
            <FormattedMessage id="BusRequestForm.body" />
          </p>

          <FinalForm
            onSubmit={handleSubmit}
            initialValues={{
              conditionPreference: CONDITION_PREFERENCE.EITHER,
              financingAssistanceRequested: 'no',
            }}
            render={formRenderProps => {
              const { handleSubmit: onFormSubmit, submitting, invalid, values } = formRenderProps;
              const consentGiven = isChecked(values.consentToContact);
              const needsOrganization = isOrganisationalBuyer(values.operatorType);
              const submitDisabled = invalid || submitting || !consentGiven;

              return (
                <Form onSubmit={onFormSubmit}>
                  <div className={css.row}>
                    <FieldTextInput
                      className={css.field}
                      type="text"
                      id="BusRequestForm.name"
                      name="name"
                      label={intl.formatMessage({ id: 'BusRequestForm.nameLabel' })}
                      validate={requiredField('BusRequestForm.nameRequired')}
                    />
                    <FieldSelect
                      className={css.field}
                      id="BusRequestForm.operatorType"
                      name="operatorType"
                      label={intl.formatMessage({ id: 'BusRequestForm.operatorTypeLabel' })}
                      validate={requiredField('BusRequestForm.operatorTypeRequired')}
                    >
                      <option value="">
                        {intl.formatMessage({ id: 'BusRequestForm.operatorTypePlaceholder' })}
                      </option>
                      {Object.values(OPERATOR_TYPE).map(value => (
                        <option key={value} value={value}>
                          {intl.formatMessage({ id: `BusRequestForm.operatorType.${value}` })}
                        </option>
                      ))}
                    </FieldSelect>
                  </div>

                  {/* A band, division or operator buys as the organisation. A
                      private individual is not asked for one. */}
                  {needsOrganization ? (
                    <FieldTextInput
                      className={css.fieldFull}
                      type="text"
                      id="BusRequestForm.organization"
                      name="organization"
                      label={intl.formatMessage({ id: 'BusRequestForm.organizationLabel' })}
                      validate={requiredField('BusRequestForm.organizationRequired')}
                    />
                  ) : null}

                  <div className={css.row}>
                    <FieldTextInput
                      className={css.field}
                      type="email"
                      id="BusRequestForm.email"
                      name="email"
                      label={intl.formatMessage({ id: 'BusRequestForm.emailLabel' })}
                      validate={emailValidators}
                    />
                    <FieldTextInput
                      className={css.field}
                      type="tel"
                      id="BusRequestForm.phone"
                      name="phone"
                      label={intl.formatMessage({ id: 'BusRequestForm.phoneLabel' })}
                      validate={requiredField('BusRequestForm.phoneRequired')}
                    />
                  </div>

                  <div className={css.row}>
                    <FieldSelect
                      className={css.field}
                      id="BusRequestForm.busType"
                      name="busType"
                      label={intl.formatMessage({ id: 'BusRequestForm.busTypeLabel' })}
                      validate={requiredField('BusRequestForm.busTypeRequired')}
                    >
                      <option value="">
                        {intl.formatMessage({ id: 'BusRequestForm.busTypePlaceholder' })}
                      </option>
                      {Object.values(BUS_TYPES).map(value => (
                        <option key={value} value={value}>
                          {BUS_TYPE_LABELS[value]}
                        </option>
                      ))}
                    </FieldSelect>
                    <FieldTextInput
                      className={css.field}
                      type="number"
                      id="BusRequestForm.passengerCapacity"
                      name="passengerCapacity"
                      label={intl.formatMessage({ id: 'BusRequestForm.capacityLabel' })}
                      placeholder={intl.formatMessage({ id: 'BusRequestForm.capacityPlaceholder' })}
                    />
                  </div>

                  <fieldset className={css.radioFieldset}>
                    <legend className={css.legend}>
                      <FormattedMessage id="BusRequestForm.conditionLabel" />
                    </legend>
                    <div className={css.radioRow}>
                      {Object.values(CONDITION_PREFERENCE).map(value => (
                        <FieldRadioButton
                          key={value}
                          id={`BusRequestForm.condition.${value}`}
                          name="conditionPreference"
                          value={value}
                          label={intl.formatMessage({ id: `BusRequestForm.condition.${value}` })}
                        />
                      ))}
                    </div>
                  </fieldset>

                  <FieldSelect
                    className={css.fieldFull}
                    id="BusRequestForm.budgetBand"
                    name="budgetBand"
                    label={intl.formatMessage({ id: 'BusRequestForm.budgetLabel' })}
                  >
                    <option value="">
                      {intl.formatMessage({ id: 'BusRequestForm.budgetPlaceholder' })}
                    </option>
                    {BUDGET_BANDS.map(band => (
                      <option key={band.id} value={band.id}>
                        {band.label}
                      </option>
                    ))}
                  </FieldSelect>

                  {/* Province or community — one or the other. A fly-in
                      community may be the only address a buyer can give, and
                      demanding a province code would turn away exactly the
                      buyers this category exists to reach. */}
                  <div className={css.row}>
                    <FieldSelect
                      className={css.field}
                      id="BusRequestForm.province"
                      name="province"
                      label={intl.formatMessage({ id: 'BusRequestForm.provinceLabel' })}
                    >
                      <option value="">
                        {intl.formatMessage({ id: 'BusRequestForm.provincePlaceholder' })}
                      </option>
                      {PROVINCES.map(p => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </FieldSelect>
                    <FieldTextInput
                      className={css.field}
                      type="text"
                      id="BusRequestForm.community"
                      name="community"
                      label={intl.formatMessage({ id: 'BusRequestForm.communityLabel' })}
                      placeholder={intl.formatMessage({
                        id: 'BusRequestForm.communityPlaceholder',
                      })}
                    />
                  </div>
                  <p className={css.help}>
                    <FormattedMessage id="BusRequestForm.locationHelp" />
                  </p>

                  <FieldTextInput
                    className={css.fieldFull}
                    type="textarea"
                    id="BusRequestForm.deliveryNotes"
                    name="deliveryNotes"
                    label={intl.formatMessage({ id: 'BusRequestForm.deliveryLabel' })}
                    placeholder={intl.formatMessage({ id: 'BusRequestForm.deliveryPlaceholder' })}
                  />

                  <fieldset className={css.radioFieldset}>
                    <legend className={css.legend}>
                      <FormattedMessage id="BusRequestForm.financingLabel" />
                    </legend>
                    <div className={css.radioRow}>
                      <FieldRadioButton
                        id="BusRequestForm.financing.yes"
                        name="financingAssistanceRequested"
                        value="yes"
                        label={intl.formatMessage({ id: 'BusRequestForm.financingYes' })}
                      />
                      <FieldRadioButton
                        id="BusRequestForm.financing.no"
                        name="financingAssistanceRequested"
                        value="no"
                        label={intl.formatMessage({ id: 'BusRequestForm.financingNo' })}
                      />
                    </div>
                    <p className={css.help}>
                      <FormattedMessage id="BusRequestForm.financingHelp" />
                    </p>
                  </fieldset>

                  <div className={css.consentBlock}>
                    <FieldCheckbox
                      id="BusRequestForm.consentToContact"
                      name="consentToContact"
                      value="given"
                      label={intl.formatMessage({ id: 'BusRequestForm.consentToContact' })}
                    />
                  </div>

                  {submitError ? (
                    <p className={css.error} role="alert">
                      {submitError}
                    </p>
                  ) : null}

                  <PrimaryButton
                    className={css.submitButton}
                    type="submit"
                    inProgress={submitting}
                    disabled={submitDisabled}
                  >
                    <FormattedMessage id="BusRequestForm.submitButton" />
                  </PrimaryButton>

                  <p className={css.compliance}>
                    <FormattedMessage id="BusRequestForm.compliance" />
                  </p>
                </Form>
              );
            }}
          />
        </>
      )}
    </Modal>
  );
}
